import React, { useEffect, useState } from "react";
import DataTable from "../../../Common/DataTable/DataTable";
import DropDown from "../../../Common/DropDown/index";
import { Offcanvas } from "react-bootstrap";
import Button from "../../../Common/Button";
import styles from "./partnerRates.module.css";
import withFeatureActions from "../../../withFeatureActions";
import Input from "../../../Common/V2/Widgets/Input";
import { CookieUtils } from "../../../axios.config";
import {
  getUsersPartnerList,
  partnerRates,
  partnerRatesChange,
} from "../../../api";
import { partnerRatesColumns } from "../../../utils/tableHeader";
import { productType } from "../../../utils/utils";

const PartnerRates = ({ featureAccess }) => {
  const clientName = CookieUtils.get("clientName");
  const defaultPartner = clientName === "STOCKAL" ? "STOCKAL" : clientName;
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(defaultPartner);
  const [tableData, setTableData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const [formError, setFormError] = useState("");
  const [showCanvas, setShowCanvas] = useState(false);
  const [editData, setEditData] = useState(null);
  const [stockalMarkup, setStockalMarkup] = useState("");
  const [desiredMarkup, setDesiredMarkup] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [desiredMarkupError, setDesiredMarkupError] = useState("");
  const [loading, setLoading] = useState(false);
  const columns = partnerRatesColumns(featureAccess);

  const fetchMcbPartners = async () => {
    const resp = await getUsersPartnerList({ appName: productType.MCB });

    const partners =
      resp?.data?.data?.map((item) => ({
        label: item.partnerCd,
        value: item.partnerCd,
      })) || [];

    setPartnerList(partners);
  };

  const fetchPartnerRates = async (partnerCode) => {
    if (!partnerCode) {
      setTableData([]);
      return;
    }

    setDataLoading(true);
    setTableError("");
    try {
      const resp = await partnerRates(partnerCode);
      if (resp?.status === 404) {
        setTableData([]);
        setTableError(resp?.data?.message || "Partner plan not found");
        return;
      }

      if (resp?.status === 200) {
        const list = resp?.data?.data || [];

        const formatted = list.map((item) => ({
          ...item,
          currencies: item.currencies?.join(", "),
        }));

        setTableData(formatted);
      }
    } catch (err) {
      setTableData([]);
      setTableError(err?.response?.data?.message || "Failed to fetch rates");
    } finally {
      setDataLoading(false);
    }
  };

  const handlePartnerChange = (val) => {
    setSelectedPartner(val);
  };

  const calculateStockal = (desired, vendor) => {
    const rawMarkup = ((1 + desired / 100) / (1 + vendor / 100) - 1) * 100;

    const stockal = Number(rawMarkup.toFixed(4));

    setStockalMarkup(stockal);
  };

  const handleDesiredChange = (e) => {
    const value = e.target.value;
    setDesiredMarkup(value);

    if (value === "" || value === null) {
      setDesiredMarkupError("Desired Markup is required");
    } else if (Number(value) < 0 || Number(value) > 100) {
      setDesiredMarkupError("Desired Markup must be between 0 and 100");
    } else {
      setDesiredMarkupError("");
    }

    if (editData && value !== "") {
      calculateStockal(Number(value), Number(editData.vendorMarkup));
    }
  };

  const handleEditPartnerRates = (item) => {
    setEditData(item);

    setDesiredMarkup(Number(item.desiredMarkup ?? 0).toFixed(4));
    setStockalMarkup(Number(item.stockalMarkup ?? 0).toFixed(4));
    setSuccessMessage("");
    setFormError("");
    setDesiredMarkupError("");

    setShowCanvas(true);
  };

  const handleCloseCanvas = () => {
    setShowCanvas(false);

    setSuccessMessage("");
    setFormError("");
    setDesiredMarkupError("");
  };

  const handleSave = async () => {
    if (desiredMarkup === "" || desiredMarkup === null) {
      setDesiredMarkupError("Desired Markup is required");
      return;
    }

    if (Number(desiredMarkup) < 0 || Number(desiredMarkup) > 100) {
      setDesiredMarkupError("Desired Markup must be between 0 and 100");
      return;
    }

    const payload = {
      partnerCode: selectedPartner,
      rates: [
        {
          currencyGroup: editData.currencyGroup,
          desiredMarkup: Number(desiredMarkup),
        },
      ],
    };

    try {
      setLoading(true);
      setFormError("");

      const resp = await partnerRatesChange(payload);
      if (resp?.status === 400) {
        setFormError(resp?.data?.message || "Something went wrong");
        return;
      }

      if (resp?.status === 200) {
        setSuccessMessage("Rates Updated Successfully");

        setTimeout(() => {
          handleCloseCanvas();
          fetchPartnerRates(selectedPartner);
        }, 3000);
      }
    } catch (err) {
      setFormError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMcbPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchPartnerRates(selectedPartner);
    }
  }, [selectedPartner]);

  return (
    <div className={styles.container}>
      <div className={styles.tablecontainer}>
        <DataTable
          title="Partner Rates"
          columns={columns}
          list={tableData}
          loading={dataLoading}
          errorMessage={tableError}
          handleUpdatePartnerRates={handleEditPartnerRates}
          titleDropdown={
            <DropDown
              label="Select Partner"
              name="partner"
              value={selectedPartner}
              data={partnerList}
              onChange={handlePartnerChange}
              placeholder="Select Partner"
              className={styles.partnerDropdown}
            />
          }
        />

        <Offcanvas
          show={showCanvas}
          onHide={handleCloseCanvas}
          placement="end"
          className={styles.offcanvas}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ color: "white" }}>
              Edit Partner Rates
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            {editData && (
              <div className={styles.formContainer}>
                <Input
                  label="Currency Group"
                  name="currencyGroup"
                  value={editData.currencyGroup}
                  disabled
                  className={styles.field}
                />

                <Input
                  label="Vendor Markup"
                  name="vendorMarkup"
                  value={editData.vendorMarkup}
                  disabled
                  className={styles.field}
                />

                <Input
                  label="Desired Markup"
                  name="desiredMarkup"
                  type="number"
                  value={desiredMarkup}
                  onChange={handleDesiredChange}
                  error={!!desiredMarkupError}
                  helperText={desiredMarkupError}
                  className={styles.field}
                  required
                />

                <Input 
                  label="Stockal Markup" 
                  name="stockalMarkup"
                  value={stockalMarkup} 
                  disabled 
                  className={styles.field}
                  />

                <div className={styles.buttonWrapper}>
                  {successMessage && (
                    <div className={styles.message}>{successMessage}</div>
                  )}

                  {formError && (
                    <div className={styles.messageError}>{formError}</div>
                  )}
                  <div style={{ width: "150px" }}>
                    <Button
                      title="Save"
                      onClick={handleSave}
                      loading={loading}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </div>
  );
};

export default withFeatureActions(PartnerRates, "MCB", "Partner Rates");
